import React, { useEffect, useState } from 'react'
import { Modal, Form, Button, Card, Alert, Spinner, Row, Col } from 'react-bootstrap'
import { Channel, ChannelProvider, ChannelType } from '../../../types/assignmentGroups'
import { MultiSelectWithActions } from '../../../components/MultiSelectWithActions'

interface ChannelFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: Partial<Channel>) => Promise<void>
  saving: boolean
  initialData?: Channel | null
  groupMembers: Array<{ value: string; label: string }>
}

export const ChannelFormModal: React.FC<ChannelFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  saving,
  initialData,
  groupMembers,
}) => {
  const [provider] = useState<ChannelProvider>('TWILIO')
  const [channelType, setChannelType] = useState<ChannelType>('SMS')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [assignedMemberIds, setAssignedMemberIds] = useState<string[]>([])
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [savedChannelId, setSavedChannelId] = useState<number | null>(null)
  const [testingCredentials, setTestingCredentials] = useState(false)
  const [testMessage, setTestMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Initialize from initialData or reset
  useEffect(() => {
    if (initialData) {
      setPhoneNumber(initialData.phone_number)
      setIsActive(initialData.is_active)
      setAssignedMemberIds(initialData.assigned_member_ids.map((id) => id.toString()))
      setCredentials(initialData.credentials || {})
      setSavedChannelId(initialData.id)
    } else {
      setPhoneNumber('')
      setIsActive(true)
      setAssignedMemberIds([])
      setCredentials({})
      setSavedChannelId(null)
    }
    setError(null)
  }, [initialData, isOpen])

  const handleSave = async () => {
    setError(null)

    // Validate Twilio credentials
    const requiredFields = ['account_sid', 'auth_token']
    const missingFields = requiredFields.filter((field: string) => !credentials[field])
    if (missingFields.length > 0) {
      setError(`Missing required fields: ${missingFields.join(', ')}`)
      return
    }

    try {
      const data: Partial<Channel> = {
        id: savedChannelId || undefined,
        provider,
        channel_type: channelType,
        phone_number: phoneNumber,
        is_active: isActive,
        assigned_member_ids: assignedMemberIds.map((id) => parseInt(id, 10)),
        credentials,
      }

      await onSave(data)
      onClose()
    } catch (err) {
      setError('Error saving channel')
      console.error(err)
    }
  }

  const handleCredentialChange = (field: string, value: string) => {
    setCredentials((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleTestCredentials = async () => {
    setTestMessage(null)
    const missingFields = ['account_sid', 'auth_token'].filter((field) => !credentials[field])

    if (missingFields.length > 0) {
      setTestMessage({
        type: 'error',
        text: `Missing fields: ${missingFields.join(', ')}`,
      })
      return
    }

    setTestingCredentials(true)
    try {
      // Aquí iría la llamada a la API para validar credenciales de Twilio
      // Por ahora solo simulamos el test
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setTestMessage({
        type: 'success',
        text: 'Credentials verified successfully!',
      })
    } catch (err) {
      setTestMessage({
        type: 'error',
        text: 'Failed to verify credentials. Please check and try again.',
      })
    } finally {
      setTestingCredentials(false)
    }
  }

  const getWebhookUrl = () => {
    if (!savedChannelId) return null
    if (provider === 'TWILIO') {
      return `https://app.policytools.com/webhooks/twilio/${credentials.webhook_token || '[token]'}/`
    }
    if (provider === 'BOTMAKER') {
      return `https://app.policytools.com/webhooks/botmaker/${credentials.webhook_token || '[token]'}/`
    }
    return null
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='lg' backdrop='static'>
      <Modal.Header closeButton>
        <Modal.Title>
          {savedChannelId ? 'Edit Twilio Channel' : 'Add Twilio Channel'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant='danger' className='mb-3'>{error}</Alert>}

        <Form>
          <Row className='g-3 mb-3'>
            <Col md={6}>
              <Form.Group>
                <Form.Label className='fw-semibold'>Channel Type</Form.Label>
                <Form.Select value={channelType} onChange={(e) => setChannelType(e.target.value as ChannelType)} size='sm'>
                  <option value='SMS'>SMS</option>
                  <option value='WHATSAPP'>WhatsApp</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className='fw-semibold'>Twilio Phone Number</Form.Label>
                <Form.Control
                  type='text'
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder='+1234567890'
                  size='sm'
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className='mb-3'>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <Form.Label className='fw-semibold mb-0'>Credentials</Form.Label>
              <Form.Switch
                id='isActive'
                label='Active'
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className='fw-semibold'
              />
            </div>
            <Card className='bg-light p-3'>
              <Row className='g-3 mb-3'>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className='fw-semibold' style={{ fontSize: '0.875rem' }}>Account SID</Form.Label>
                    <Form.Control
                      type='text'
                      value={credentials.account_sid || ''}
                      onChange={(e) => handleCredentialChange('account_sid', e.target.value)}
                      placeholder='Your Twilio Account SID'
                      size='sm'
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className='fw-semibold' style={{ fontSize: '0.875rem' }}>Auth Token</Form.Label>
                    <Form.Control
                      type='password'
                      value={credentials.auth_token || ''}
                      onChange={(e) => handleCredentialChange('auth_token', e.target.value)}
                      placeholder='Your Twilio Auth Token'
                      size='sm'
                    />
                  </Form.Group>
                </Col>
                <Col md={2} className='d-flex align-items-end'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleTestCredentials}
                    disabled={testingCredentials}
                    className='w-100'
                  >
                    {testingCredentials ? (
                      <>
                        <Spinner as='span' animation='border' size='sm' />
                      </>
                    ) : (
                      'Test'
                    )}
                  </Button>
                </Col>
              </Row>
              {testMessage && (
                <Alert variant={testMessage.type === 'success' ? 'success' : 'danger'} className='mb-0'>
                  {testMessage.text}
                </Alert>
              )}
            </Card>
          </Form.Group>

          {groupMembers.length > 0 && (
            <Form.Group className='mb-3'>
              <Form.Label className='fw-semibold'>Assigned Members</Form.Label>
              <Form.Text className='d-block text-muted mb-2'>
                Empty = all group members. Select to assign to specific members only.
              </Form.Text>
              <MultiSelectWithActions
                value={assignedMemberIds}
                onChange={setAssignedMemberIds}
                options={groupMembers}
                placeholder='Select members...'
              />
            </Form.Group>
          )}

          {savedChannelId && getWebhookUrl() && (
            <>
              <Card className='bg-info-light p-3 mb-3'>
                <Form.Group>
                  <Form.Label className='fw-semibold mb-2'>Webhook URL (Inbound Messages)</Form.Label>
                  <Form.Control
                    type='text'
                    readOnly
                    value={getWebhookUrl() || ''}
                    onClick={(e) => {
                      const input = e.currentTarget
                      input.select()
                      document.execCommand('copy')
                    }}
                    title='Click to copy'
                    className='font-monospace'
                    style={{ fontSize: '0.875rem' }}
                    size='sm'
                  />
                  <Form.Text className='d-block mt-2 text-muted'>
                    Configure this URL in your {provider} webhook settings for incoming messages
                  </Form.Text>
                </Form.Group>
              </Card>

              <Card className='bg-warning-light p-3 mb-3'>
                <Form.Group>
                  <Form.Label className='fw-semibold mb-2'>Status Callback URL (Delivery Receipts)</Form.Label>
                  <Form.Control
                    type='text'
                    readOnly
                    value='https://app.policytools.com/webhooks/twilio/status/'
                    onClick={(e) => {
                      const input = e.currentTarget
                      input.select()
                      document.execCommand('copy')
                    }}
                    title='Click to copy'
                    className='font-monospace'
                    style={{ fontSize: '0.875rem' }}
                    size='sm'
                  />
                  <Form.Text className='d-block mt-2 text-muted'>
                    Configure this URL in your Twilio webhook settings for delivery status updates (✓✓)
                  </Form.Text>
                </Form.Group>
              </Card>
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner as='span' animation='border' size='sm' className='me-2' />
              Saving...
            </>
          ) : (
            'Save Channel'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
